import { useEffect, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { SelectProps } from "../../form-types";
import { cn } from "../../../lib/utils";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
} from "../../../components/ui/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "../../../components/ui/popover";
import { useUserManagementStore } from "../../../store/UserManagement";
import { Member } from "../../../store/UserManagement/user-management.store";
import { useJobStore } from "../../../store/Jobs";
import {
	getUserColor,
	getUserLightColor,
} from "../../../lib/utils/color-utils";
import { useCentralStore } from "../../../store/Central";
import { getProfileDisplayName } from "@/lib/utils/client-utils";

const STYLES = {
	BORDER_NORMAL: 4,
	BORDER_SELECTED: 8,
	MARGIN: 2,
	BORDER_RADIUS: 4,
	PADDING_LEFT: "4px",
} as const;

const generateLabel = (profile: Member): string => {
	return (
		getProfileDisplayName(profile, "Unbekannt") ||
		profile.code ||
		"Unbekannt"
	);
};

const generateInitials = (profile: Member): string => {
	const firstName = profile.first_name?.trim() || "";
	const lastName = profile.last_name?.trim() || "";
	const firstInitial = firstName.charAt(0).toUpperCase();
	const lastInitial = lastName.charAt(0).toUpperCase();
	return `${firstInitial}${lastInitial}`;
};

const getBorderStyle = (isSelected: boolean, color: string): string => {
	const width = isSelected ? STYLES.BORDER_SELECTED : STYLES.BORDER_NORMAL;
	return `${width}px solid ${color}`;
};

interface TeamProfileAutocompleteProps extends SelectProps {
	onBulkSelect?: (
		value: string | null,
		metaKeyPressed: boolean,
		shiftKeyPressed: boolean,
		altKeyPressed: boolean
	) => void;
	field?: string;
	rowIndex?: number;
	placeholder?: string;
}

export const TeamProfileAutocomplete: React.FC<TeamProfileAutocompleteProps> = (
	props
) => {
	const [open, setOpen] = useState(false);
	const [searchValue, setSearchValue] = useState("");
	const [selectedIndex, setSelectedIndex] = useState<number>(0);

	const job = useJobStore((state) => state.job);
	const { activeUsers } = useUserManagementStore((state) => ({
		activeUsers: state
			.getActiveMembers(new Date(job?.incoming_order_date ?? new Date()))
			.filter((member) => !member.is_pending_signup),
	}));
	const { organization } = useCentralStore((state) => ({
		organization: state.organization,
	}));

	const filterUsers = (users: Member[], search: string): Member[] => {
		if (!search.trim()) return users;

		const searchLower = search.toLowerCase();
		return users.filter((user) => {
			const label = generateLabel(user).toLowerCase();
			const code = user.code?.toLowerCase() || "";
			const initials = generateInitials(user).toLowerCase();
			return (
				label.includes(searchLower) ||
				code.includes(searchLower) ||
				initials.includes(searchLower)
			);
		});
	};

	const filteredUsers = filterUsers(activeUsers, searchValue);
	const totalOptions = filteredUsers.length + 1;

	useEffect(() => {
		if (open && searchValue === "") {
			if (!props.value) {
				setSelectedIndex(0);
			} else {
				const currentUserIndex = filteredUsers.findIndex(
					(user) => user.id === props.value
				);
				if (currentUserIndex >= 0) {
					setSelectedIndex(currentUserIndex + 1);
				} else {
					setSelectedIndex(0);
				}
			}
		} else if (open && searchValue !== "") {
			if (filteredUsers.length === 0) {
				setSelectedIndex(0);
			} else {
				setSelectedIndex(1);
			}
		}
	}, [open, searchValue]);

	const handleSelect = (
		userId: string | null | undefined,
		metaKeyPressed: boolean = false,
		shiftKeyPressed: boolean = false,
		altKeyPressed: boolean = false,
		closePopover: boolean = true
	) => {
		const value = userId === undefined ? null : userId;
		if (props.onBulkSelect) {
			props.onBulkSelect(
				value,
				metaKeyPressed,
				shiftKeyPressed,
				altKeyPressed
			);
		} else {
			props.onChange(value);
		}
		if (closePopover) {
			setOpen(false);
		}
		setSearchValue("");
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		switch (e.key) {
			case "Enter":
				e.preventDefault();
				const metaKeyPressed = e.metaKey || e.ctrlKey;
				const shiftKeyPressed = e.shiftKey;
				const altKeyPressed = e.altKey;

				if (selectedIndex === 0) {
					handleSelect(
						null,
						metaKeyPressed,
						shiftKeyPressed,
						altKeyPressed
					);
				} else if (filteredUsers.length > 0) {
					const userIndex = selectedIndex - 1;
					const selectedUser = filteredUsers[userIndex];
					handleSelect(
						selectedUser.id,
						metaKeyPressed,
						shiftKeyPressed,
						altKeyPressed
					);
				}
				break;
			case "ArrowDown":
				e.preventDefault();
				if (e.metaKey || e.ctrlKey) {
					setSelectedIndex(totalOptions - 1);
				} else {
					setSelectedIndex((prev) =>
						prev < totalOptions - 1 ? prev + 1 : 0
					);
				}
				break;
			case "ArrowUp":
				e.preventDefault();
				if (e.metaKey || e.ctrlKey) {
					setSelectedIndex(0);
				} else {
					setSelectedIndex((prev) =>
						prev > 0 ? prev - 1 : totalOptions - 1
					);
				}
				break;
			case "Escape":
				e.preventDefault();
				setOpen(false);
				setSearchValue("");
				break;
			case "Backspace":
				if (searchValue != "") {
					e.stopPropagation();
					break;
				}
			case "ArrowRight":
			case "ArrowLeft":
				e.preventDefault();
				break;
			default:
				break;
		}
	};

	const selectedUser = activeUsers.find((user) => user.id === props.value);

	const getTriggerStyle = () => {
		if (!selectedUser?.id) {
			return {
				paddingLeft: STYLES.PADDING_LEFT,
			};
		}

		const customColors =
			(organization?.profile_colors as Record<string, string>) || {};
		const selectedColor = getUserColor(selectedUser.id, customColors);
		const selectedLightColor = getUserLightColor(
			selectedUser.id,
			customColors
		);

		return {
			backgroundColor: selectedLightColor,
			borderLeft: getBorderStyle(false, selectedColor),
			paddingLeft: STYLES.PADDING_LEFT,
		};
	};

	const handleOpenChange = (newOpen: boolean) => {
		setOpen(newOpen);
		if (!newOpen) {
			setSearchValue("");
		}
	};

	return (
		<div className="w-full">
			<Popover open={open} onOpenChange={handleOpenChange}>
				<PopoverTrigger asChild>
					<button
						className={cn(
							"w-full text-xs text-left whitespace-nowrap overflow-hidden text-ellipsis",
							props.placeholder
								? "h-7 flex items-center justify-between gap-1 rounded-md border border-input bg-background px-2 hover:bg-accent hover:text-accent-foreground"
								: "h-5 p-0 border-0",
							props.placeholder &&
								!selectedUser &&
								"text-muted-foreground"
						)}
						style={getTriggerStyle()}
						title={selectedUser ? generateLabel(selectedUser) : ""}
						disabled={props.disabled}
						aria-label="Techniker auswählen"
						data-testid="technician-autocomplete-trigger"
						data-field={props.field}
						data-row-index={props.rowIndex}
					>
						{props.placeholder ? (
							<>
								<span className="truncate">
									{selectedUser
										? generateLabel(selectedUser)
										: props.placeholder}
								</span>
								<ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
							</>
						) : selectedUser ? (
							generateLabel(selectedUser)
						) : (
							""
						)}
					</button>
				</PopoverTrigger>
				<PopoverContent
					className="w-64 p-0"
					side="right"
					align="start"
					onKeyDown={handleKeyDown}
				>
					<Command shouldFilter={false}>
						<CommandInput
							placeholder="Techniker suchen..."
							value={searchValue}
							onValueChange={setSearchValue}
						/>
						<CommandEmpty>Keine Techniker gefunden</CommandEmpty>
						<CommandGroup className="max-h-[30vh] overflow-y-auto">
							<CommandItem
								value=""
								onSelect={() => handleSelect(null)}
								onMouseDown={(e) => {
									const metaKeyPressed =
										e.metaKey || e.ctrlKey;
									const shiftKeyPressed = e.shiftKey;
									const altKeyPressed = e.altKey;
									if (
										metaKeyPressed ||
										shiftKeyPressed ||
										altKeyPressed
									) {
										e.preventDefault();
										handleSelect(
											null,
											metaKeyPressed,
											shiftKeyPressed,
											altKeyPressed
										);
									}
								}}
								onMouseEnter={() => setSelectedIndex(0)}
								className="pl-2 pr-2 transition-colors duration-200"
								style={{
									margin: `${STYLES.MARGIN}px`,
									borderRadius: `${STYLES.BORDER_RADIUS}px`,
									backgroundColor:
										selectedIndex === 0
											? "#f3f4f6"
											: "transparent",
								}}
							>
								<em>Kein Techniker</em>
							</CommandItem>
							{filteredUsers.map((user, index) => {
								if (!user.id) return null;

								const isCurrentlySelected =
									props.value === user.id;
								const isKeyboardHighlighted =
									index + 1 === selectedIndex;
								const customColors =
									(organization?.profile_colors as Record<
										string,
										string
									>) || {};
								const userColor = getUserColor(
									user.id,
									customColors
								);
								const userLightColor = getUserLightColor(
									user.id,
									customColors
								);

								return (
									<CommandItem
										key={user.id}
										value={user.id}
										onSelect={(value) => {
											handleSelect(
												value === "" ? null : value
											);
										}}
										onMouseDown={(e) => {
											const metaKeyPressed =
												e.metaKey || e.ctrlKey;
											const shiftKeyPressed = e.shiftKey;
											const altKeyPressed = e.altKey;
											if (
												metaKeyPressed ||
												shiftKeyPressed ||
												altKeyPressed
											) {
												e.preventDefault();
												handleSelect(
													user.id,
													metaKeyPressed,
													shiftKeyPressed,
													altKeyPressed
												);
											}
										}}
										onMouseEnter={() =>
											setSelectedIndex(index + 1)
										}
										className="pl-2 pr-2 relative transition-all duration-200 cursor-pointer flex items-center justify-between"
										style={{
											backgroundColor:
												isKeyboardHighlighted
													? userLightColor
													: isCurrentlySelected
														? userLightColor
														: "transparent",
											borderLeft: getBorderStyle(
												isKeyboardHighlighted,
												userColor
											),
											margin: `${STYLES.MARGIN}px`,
											borderRadius: `${STYLES.BORDER_RADIUS}px`,
											opacity:
												isCurrentlySelected &&
												!isKeyboardHighlighted
													? 0.7
													: 1,
										}}
									>
										<span>{generateLabel(user)}</span>
										{isCurrentlySelected && (
											<Check
												className="h-3 w-3 shrink-0 ml-2"
												style={{ color: userColor }}
											/>
										)}
									</CommandItem>
								);
							})}
						</CommandGroup>
					</Command>
				</PopoverContent>
			</Popover>
		</div>
	);
};
